import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Category } from "../lib/types";

interface Props {
  categories: Category[];
  active: string;
  onChange: (id: string) => void;
}

export function CategoryRow({ categories, active, onChange }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
      window.removeEventListener("resize", updateArrows);
    };
  }, [categories.length]);

  const scrollBy = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: "smooth" });
  };

  // Translate vertical wheel into horizontal scroll on desktop pointers.
  const onWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollBy({ left: e.deltaY, behavior: "auto" });
    }
  };

  return (
    <div className="relative">
      {canLeft && (
        <button
          type="button"
          aria-label="Scroll categories left"
          onClick={() => scrollBy(-1)}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center bg-canvas/85 backdrop-blur-sm border border-muted/40 text-text-primary hover:text-accent-gold hover:border-accent-gold transition-fast shadow-md"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          aria-label="Scroll categories right"
          onClick={() => scrollBy(1)}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full items-center justify-center bg-canvas/85 backdrop-blur-sm border border-muted/40 text-text-primary hover:text-accent-gold hover:border-accent-gold transition-fast shadow-md"
        >
          <ChevronRight size={18} />
        </button>
      )}
      <div
        ref={scrollerRef}
        onWheel={onWheel}
        className="overflow-x-auto no-scrollbar -mx-4 px-4 lg:mx-0 lg:px-0 scroll-smooth"
      >
        <div className="flex items-center gap-2 py-1 md:px-9">
          <Pill label="All" active={active === "All"} onClick={() => onChange("All")} />
          {categories.map((c) => (
            <Pill
              key={c.id}
              label={c.name}
              active={active === c.id}
              onClick={() => onChange(c.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Pill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 px-3.5 py-1.5 rounded-pill text-[12px] font-ui font-medium uppercase tracking-[0.04em] transition-fast border ${
        active
          ? "bg-accent-gold text-canvas border-accent-gold shadow-[0_0_8px_rgba(224,172,72,0.35)]"
          : "border-muted/40 text-text-secondary hover:text-text-primary hover:border-text-secondary"
      }`}
    >
      {label}
    </button>
  );
}
